// Foto-evidencia de una tarea (F4 plan de tareas, 17-ago-2026) — port del bloque de la
// agenda dental con los tokens de Let's Move: cámara + galería + puente QR + quitar.
// El candado real vive en el backend (completar sin foto = 400); aquí solo UX.
import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { Camera, Image as ImageIcon, Trash2, QrCode, Loader2 } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import { fileToDataUrl } from '../../utils/imageCompress';
import { uploadTaskPhoto, deleteTaskPhoto, evidenceLink, Task } from './api';

export default function PhotoEvidence({ task, onChanged }: { task: Task; onChanged: () => void }) {
  const toast = useToast();
  const camRef = useRef<HTMLInputElement>(null);
  const galRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const open = task.status === 'open';
  // PUENTE QR: la compu de recepción no tiene cámara — token corto y el celular sube la
  // foto por /evidencia/:token SIN sesión. Mientras el QR está visible se re-consulta la
  // lista cada 4s; al llegar la foto (photo_path cambia) el QR muere solo.
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!qrUrl) return;
    const iv = setInterval(onChanged, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrUrl]);
  useEffect(() => {
    if (task.photo_path && qrUrl) { setQrUrl(null); toast.success('¡Foto recibida del celular!'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.photo_path]);

  const showQr = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await evidenceLink(task.task_id);
      setQrUrl(`${window.location.origin}/evidencia/${r.token}`);
    } catch (e: any) { toast.error(e?.message || 'No se pudo generar el QR'); }
    finally { setBusy(false); }
  };

  // La URL de la foto es fija por tarea: tras re-tomar hay que romper la caché del <img>.
  const [ver, setVer] = useState(0);
  const photoSrc = task.photo_path ? `${task.photo_path}${ver ? `?v=${ver}` : ''}` : null;

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      await uploadTaskPhoto(task.task_id, dataUrl);
      toast.success('Evidencia guardada');
      setVer((v) => v + 1);
      onChanged();
    } catch (e: any) { toast.error(e?.message || 'No se pudo subir la foto'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    try { await deleteTaskPhoto(task.task_id); setVer((v) => v + 1); onChanged(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo quitar'); }
  };

  const chip = 'flex h-8 items-center gap-1 rounded-lg border border-line px-2 text-xs font-medium text-content-2 transition hover:text-content';
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} />
      <input ref={galRef} type="file" accept="image/*" className="hidden" onChange={pick} />
      {photoSrc ? (
        <>
          <a href={photoSrc} target="_blank" rel="noreferrer" title="Ver evidencia completa">
            <img src={photoSrc} alt="Evidencia" className="h-16 w-16 rounded-lg border border-line object-cover" />
          </a>
          {open && (
            <>
              <button type="button" onClick={() => camRef.current?.click()} disabled={busy} className={chip}>
                <Camera size={13} /> Re-tomar
              </button>
              <button type="button" onClick={remove}
                className="flex h-8 items-center gap-1 rounded-lg border border-line px-2 text-xs text-content-2 transition hover:border-rose-400 hover:text-rose-500">
                <Trash2 size={13} /> Quitar
              </button>
            </>
          )}
        </>
      ) : open ? (
        <>
          <button type="button" onClick={() => camRef.current?.click()} disabled={busy}
            className={`flex h-8 items-center gap-1 rounded-lg px-2.5 text-xs font-semibold transition ${task.requires_photo ? 'bg-accent text-on-accent hover:opacity-90' : chip}`}>
            <Camera size={13} /> {busy ? 'Subiendo…' : 'Tomar foto'}
          </button>
          <button type="button" onClick={() => galRef.current?.click()} disabled={busy} className={chip}>
            <ImageIcon size={13} /> Galería
          </button>
          <button type="button" onClick={() => (qrUrl ? setQrUrl(null) : showQr())} aria-pressed={!!qrUrl}
            title="Muestra un QR para tomar la foto con el celular (sin iniciar sesión en él)"
            className={`flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-medium transition ${qrUrl ? 'border-accent/50 bg-accent/15 text-accent-ink' : 'border-line text-content-2 hover:text-content'}`}>
            <QrCode size={13} /> QR celular
          </button>
          {task.requires_photo === 1 && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-600">requerida para completar</span>
          )}
          {qrUrl && (
            <div className="w-full rounded-lg border border-line bg-white p-3 text-center">
              {/* Fondo blanco fijo: el QR debe leerse igual en tema oscuro */}
              <QRCode value={qrUrl} size={148} className="mx-auto" />
              <p className="mt-2 flex items-center justify-center gap-1.5 text-xs text-gray-600">
                <Loader2 size={12} className="animate-spin" /> Escanéalo con el celular — esperando la foto…
              </p>
              <p className="mt-0.5 text-[10px] text-gray-400">El enlace vence en 20 min · <button type="button" onClick={() => setQrUrl(null)} className="underline">cancelar</button></p>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
